import { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { Add, MenuBookOutlined } from "@mui/icons-material";
import { useAppSelector } from "@store/hooks";
import { selectIsCurrentUserWorkspaceOwner } from "@store/selectors";
import { CreateReadingModal } from "@features/workspace-readings/components/CreateReadingModal";

/**
 * Purposeful empty state for a signed-in workspace with no readings —
 * shown in place of the blank studio pane, naming the next action
 * instead of leaving the user to find it (onboarding stage 1; the full
 * tour fires once the first reading is open).
 */
export default function EmptyWorkspaceState() {
  const workspaceId = useAppSelector((s) => s.workspace.workspace_id);
  const isOwner = useAppSelector(selectIsCurrentUserWorkspaceOwner);
  // A workspace always carries at least its owner's permission row, so
  // an empty list means the workspace record hasn't loaded — not that
  // the user is a contributor. Until it loads (first-boot adoption
  // race, or the API being unreachable), show neither role's copy:
  // "your workspace owner hasn't added readings" to the person who IS
  // the owner reads as a broken account.
  const permissionsLoaded = useAppSelector(
    (s) =>
      !!s.workspace.workspace_id &&
      (s.workspace.workspace_permissions?.length ?? 0) > 0
  );
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        gap: 1.5,
        px: 4,
      }}
    >
      <MenuBookOutlined sx={{ fontSize: 56, color: "text.secondary" }} />
      <Typography variant="h5">No readings yet</Typography>
      {!permissionsLoaded ? (
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 440 }}>
          Readings added to this workspace will appear here.
        </Typography>
      ) : isOwner ? (
        <>
          <Typography variant="body1" color="text.primary" sx={{ maxWidth: 440 }}>
            Everything starts with a reading: add a PDF, highlight the
            passages that matter, and build them into an argument.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateOpen(true)}
            sx={{ mt: 1 }}
          >
            Add a reading
          </Button>
          {workspaceId && (
            <CreateReadingModal
              open={createOpen}
              onClose={() => setCreateOpen(false)}
              workspaceId={workspaceId}
            />
          )}
        </>
      ) : (
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 440 }}>
          Your workspace owner hasn&apos;t added any readings yet — once
          they do, you can highlight and annotate them here.
        </Typography>
      )}
    </Box>
  );
}
